import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Link,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import FilterChips, { type FilterChipOption } from '../components/FilterChips';
import { billingApi } from '../services/billingApi';
import { usePractice } from '../contexts/PracticeContext';
import type { JobDto, JobStatus } from '../types/billing';

type StatusFilter = JobStatus | 'ALL';

type MuiChipColor = 'default' | 'info' | 'success' | 'error';

const JOB_STATUS_LABEL: Record<JobStatus, string> = {
  PENDING: 'Pending',
  RUNNING: 'Running',
  DONE: 'Done',
  FAILED: 'Failed',
};

const JOB_STATUS_COLOR: Record<JobStatus, MuiChipColor> = {
  PENDING: 'default',
  RUNNING: 'info',
  DONE: 'success',
  FAILED: 'error',
};

const POLL_INTERVAL_MS = 5000;

function formatPatient(job: JobDto): string {
  const name = `${job.payload.patient.firstName ?? ''} ${job.payload.patient.lastName ?? ''}`.trim();
  return name.length === 0 ? '-' : name;
}

function formatPickedUpAt(value: string | null): string {
  if (value === null) return '-';
  return new Date(value).toLocaleString();
}

function sortJobs(jobs: JobDto[]): JobDto[] {
  return [...jobs].sort((a, b) => {
    if (a.pickedUpAt === null && b.pickedUpAt === null) return 0;
    if (a.pickedUpAt === null) return 1;
    if (b.pickedUpAt === null) return -1;
    return b.pickedUpAt.localeCompare(a.pickedUpAt);
  });
}

const Status: React.FC = () => {
  const { currentPracticeId } = usePractice();
  const [jobs, setJobs] = useState<JobDto[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<StatusFilter>('ALL');

  const isInitialFetchRef = useRef<boolean>(true);

  const fetchJobs = useCallback(
    async (practiceId: string) => {
      try {
        const data = await billingApi.listJobs(practiceId);
        setJobs(data);
        setError(null);
      } catch (e) {
        const message = e instanceof Error ? e.message : String(e);
        setError(message);
      } finally {
        if (isInitialFetchRef.current) {
          setIsLoading(false);
          isInitialFetchRef.current = false;
        }
      }
    },
    [],
  );

  const reload = useCallback(() => {
    if (!currentPracticeId) return;
    isInitialFetchRef.current = true;
    setIsLoading(true);
    void fetchJobs(currentPracticeId);
  }, [currentPracticeId, fetchJobs]);

  useEffect(() => {
    if (!currentPracticeId) return;

    isInitialFetchRef.current = true;
    setIsLoading(true);
    void fetchJobs(currentPracticeId);

    let intervalId: ReturnType<typeof setInterval> | null = null;

    const startPolling = () => {
      if (intervalId !== null) return;
      intervalId = setInterval(() => {
        void fetchJobs(currentPracticeId);
      }, POLL_INTERVAL_MS);
    };

    const stopPolling = () => {
      if (intervalId !== null) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        startPolling();
      } else {
        stopPolling();
      }
    };

    if (document.visibilityState === 'visible') {
      startPolling();
    }
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [currentPracticeId, fetchJobs]);

  const counts = useMemo<Record<StatusFilter, number>>(() => {
    const c: Record<StatusFilter, number> = {
      ALL: jobs.length,
      PENDING: 0,
      RUNNING: 0,
      DONE: 0,
      FAILED: 0,
    };
    for (const j of jobs) {
      c[j.status] += 1;
    }
    return c;
  }, [jobs]);

  const options = useMemo<FilterChipOption<JobStatus>[]>(
    () => [
      { value: 'ALL', label: 'All', count: counts.ALL },
      { value: 'PENDING', label: 'Pending', count: counts.PENDING },
      { value: 'RUNNING', label: 'Running', count: counts.RUNNING },
      { value: 'DONE', label: 'Done', count: counts.DONE },
      { value: 'FAILED', label: 'Failed', count: counts.FAILED },
    ],
    [counts],
  );

  const visibleJobs = useMemo(() => {
    const filtered = filter === 'ALL' ? jobs : jobs.filter((j) => j.status === filter);
    return sortJobs(filtered);
  }, [jobs, filter]);

  if (!currentPracticeId) {
    return <Alert severity="info">Select a practice first.</Alert>;
  }

  const showInitialError = error !== null && jobs.length === 0;
  const showPollingError = error !== null && jobs.length > 0;
  const showEmpty = !isLoading && error === null && jobs.length === 0;

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Status
      </Typography>

      <FilterChips<JobStatus> options={options} value={filter} onChange={setFilter} />

      {isLoading && (
        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Patient</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Picked up at</TableCell>
                <TableCell>Detail</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={`skeleton-${i}`}>
                  <TableCell><Skeleton variant="text" animation="wave" /></TableCell>
                  <TableCell><Skeleton variant="text" animation="wave" /></TableCell>
                  <TableCell><Skeleton variant="text" animation="wave" /></TableCell>
                  <TableCell><Skeleton variant="text" animation="wave" /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {showInitialError && (
        <Alert
          severity="error"
          sx={{ mt: 2 }}
          action={
            <Button color="inherit" size="small" onClick={reload}>
              Retry
            </Button>
          }
        >
          <AlertTitle>Could not load jobs</AlertTitle>
          {error}
        </Alert>
      )}

      {showPollingError && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          Polling failed: {error}
        </Alert>
      )}

      {showEmpty && (
        <Paper sx={{ p: 4, textAlign: 'center', mt: 2 }}>
          <InboxOutlinedIcon sx={{ fontSize: 64, color: 'text.disabled' }} />
          <Typography variant="h6" sx={{ mt: 1 }}>
            No jobs yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Dispatched claims will appear here while the Chrome extension submits them to Office Ally.
          </Typography>
        </Paper>
      )}

      {!isLoading && jobs.length > 0 && (
        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Patient</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Picked up at</TableCell>
                <TableCell>Detail</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleJobs.map((row) => (
                <TableRow key={row.jobId} hover>
                  <TableCell>{formatPatient(row)}</TableCell>
                  <TableCell>
                    <Chip
                      label={JOB_STATUS_LABEL[row.status]}
                      color={JOB_STATUS_COLOR[row.status]}
                      size="small"
                      variant="filled"
                    />
                  </TableCell>
                  <TableCell>{formatPickedUpAt(row.pickedUpAt)}</TableCell>
                  <TableCell>
                    {row.status === 'FAILED' ? (
                      <Link component={RouterLink} to="/triage">
                        View in Triage
                      </Link>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default Status;
